import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "hooks/authHooks";
import {
  getDiagnosticOrdersListByMobile,
  getDiagnosticOrdersListByMobileVariables,
} from "graphql/types/getDiagnosticOrdersListByMobile";
import { useApolloClient } from "react-apollo-hooks";
import { GET_DIAGNOSTIC_ORDERS_LIST_BY_MOBILE } from "graphql/diagnostics";
import {
  isBrowser,
  CACHE_KEYS,
  getCacheKeyForCityId,
  getCachedDefaultDiagAddress,
} from "helpers/commonHelpers";
import { CATEGORY_WIDGET_TITLE, ORDER_CACHE_EXPIRY } from "strings/AppConfig";
import { fetchCacheUtil } from "helpers/fetch";
import {
  getWidgetsUrlForPage,
  GET_WIDGETS_PAGE_KEYS,
} from "strings/CmsUrlConfig";

import { GlobalDiagnosticsCartContextProps } from "components/interfaces/DiagnosticsCartContextProps";
import { DiagnosticWidget } from "components/Tests/TestsLanding";
import { DiagnosticAddressByPinCode } from "components/interfaces/DiagnosticAddressByPinCode";

export const DiagnosticsCartContext = createContext<GlobalDiagnosticsCartContextProps>(
  {
    getCachedOrderData: null,
    diagnosticAddress: null,
    setDiagnosticAddress: null,
    setServiceability: null,
    isAreaServiceable: false,
    cartIconClicked: null,
    setCartIconClicked: null,
    searchCategories: null,
    setSearchCategories: null,
    defaultCityId: null,
    defaultPinCode: null,
  }
);

export const DiagnosticsCartProviderGlobal: React.FC = (props) => {
  const client = useApolloClient();
  const [cartIconClicked, setCartIconClicked] = useState<boolean>(false);

  const { currentPatient } = useAuth();
  const [searchCategories, setSearchCategories] = useState<string[]>([]);
  const [isAreaServiceable, setServiceability] = useState<boolean>(true);
  const [
    diagnosticAddress,
    setDiagnosticAddress,
  ] = useState<DiagnosticAddressByPinCode>(null);
  const [defaultPinCode, setDefaultPinCode] = useState<number>(0);
  const [defaultCityId, setDefaultCityId] = useState<number>(0);

  useEffect(() => {
    populateDefaultAddress();
  }, []);

  const populateDefaultAddress = () => {
    getCachedDefaultDiagAddress()?.then((data) => {
      if (data) {
        setDefaultPinCode(Number(data?.pinCode));
        setDefaultCityId(data?.cityID);
        // if present, persist diagnosticAddress found from localStorage
        if (isBrowser() && !!localStorage?.getItem("diagnosticAddress")) {
          const localDiagAddress: DiagnosticAddressByPinCode = JSON.parse(
            localStorage?.getItem("diagnosticAddress")
          );
          setDiagnosticAddress(localDiagAddress);
        } else {
          setDiagnosticAddress(data);
        }
      }
    });
  };

  useEffect(() => {
    if (diagnosticAddress) {
      const cityId = diagnosticAddress?.cityID
        ? diagnosticAddress.cityID
        : defaultCityId;
      const url = getWidgetsUrlForPage(cityId);

      fetchCacheUtil({
        url: url,
        cacheKey: getCacheKeyForCityId(
          CACHE_KEYS.WIDGET_DATA,
          cityId,
          GET_WIDGETS_PAGE_KEYS.HOME
        ),
        ctype: "",
        useToken: true,
      }).then(({ data }) => {
        if (Array.isArray(data)) {
          const popularCategoryData: DiagnosticWidget = data?.find(
            (widgetData: DiagnosticWidget) =>
              widgetData?.diagnosticWidgetTitle?.toUpperCase() ===
              CATEGORY_WIDGET_TITLE
          );

          if (popularCategoryData?.diagnosticWidgetData?.length) {
            const allCategories = popularCategoryData?.diagnosticWidgetData?.map(
              (widgetData) => widgetData?.itemTitle
            );
            setSearchCategories(allCategories);
          }
        }
      });
    }
  }, [diagnosticAddress?.cityID]);

  useEffect(() => {
    if (diagnosticAddress) {
      setServiceability(diagnosticAddress.cityID !== 0);
      localStorage.setItem(
        "diagnosticAddress",
        JSON.stringify(diagnosticAddress)
      );
    }
  }, [diagnosticAddress]);

  const getDiagnosticOrdersListByMobile = async () => {
    const variables = {
      mobileNumber: currentPatient?.mobileNumber?.toString(),
      paginated: true,
      offset: 1,
      limit: 10,
    };
    const { data } = await client.query<
      getDiagnosticOrdersListByMobile,
      getDiagnosticOrdersListByMobileVariables
    >({
      query: GET_DIAGNOSTIC_ORDERS_LIST_BY_MOBILE,
      variables,
      fetchPolicy: "no-cache",
    });
    return data;
  };

  const getCachedOrderData = async () => {
    const cachedOrderData =
      localStorage?.getItem("cachedDiagOrderData") &&
      JSON.parse(localStorage.getItem("cachedDiagOrderData"));

    if (
      !cachedOrderData ||
      new Date()?.getTime() > cachedOrderData?.expiry ||
      !cachedOrderData?.data?.getDiagnosticOrdersListByMobile?.ordersList
        ?.length
    ) {
      const orderDataFromApi = await getDiagnosticOrdersListByMobile();
      if (orderDataFromApi) {
        localStorage.setItem(
          "cachedDiagOrderData",
          JSON.stringify({
            data: orderDataFromApi,
            expiry: new Date().getTime() + ORDER_CACHE_EXPIRY,
          })
        );
      }
      return orderDataFromApi;
    } else {
      return cachedOrderData?.data as getDiagnosticOrdersListByMobile;
    }
  };

  return (
    <DiagnosticsCartContext.Provider
      value={{
        getCachedOrderData,
        diagnosticAddress,
        setDiagnosticAddress,
        isAreaServiceable,
        setServiceability,
        cartIconClicked,
        setCartIconClicked,
        searchCategories,
        setSearchCategories,
        defaultCityId,
        defaultPinCode,
      }}
    >
      {props.children}
    </DiagnosticsCartContext.Provider>
  );
};

const useDiagnosticsContext = () =>
  useContext<GlobalDiagnosticsCartContextProps>(DiagnosticsCartContext);

export const useGlobalDiagnosticsCart = () => {
  const diagnosticsContext = useDiagnosticsContext();

  return {
    getCachedOrderData: diagnosticsContext.getCachedOrderData,
    diagnosticAddress: diagnosticsContext.diagnosticAddress,
    setDiagnosticAddress: diagnosticsContext.setDiagnosticAddress,
    setServiceability: diagnosticsContext.setServiceability,
    isAreaServiceable: diagnosticsContext.isAreaServiceable,
    cartIconClicked: diagnosticsContext.cartIconClicked,
    setCartIconClicked: diagnosticsContext.setCartIconClicked,
    searchCategories: diagnosticsContext.searchCategories,
    setSearchCategories: diagnosticsContext.setSearchCategories,
    DEFAULT_CITY_ID: diagnosticsContext.defaultCityId,
    DEFAULT_PINCODE: diagnosticsContext.defaultPinCode,
  };
};
